import React, { useEffect, useState } from "react";
import { useI18n } from "../../i18n/I18nContext";
import { api } from "../../lib/api";
import { ProductsAdmin, CouponsAdmin, MembershipsAdmin } from "./catalog";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";
import { FileDown, Printer } from "lucide-react";

// Simple reusable list page for admin modules
export function Customers() {
  const { t, fmtSAR } = useI18n();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const load = () => api.get(`/admin/customers${q ? "?q=" + q : ""}`).then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, [q]);
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.customers")}</h1>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} data-testid="customer-search" className="mt-4 w-full max-w-sm rounded-lg border border-border px-4 py-2 text-sm" />
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[560px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{[t("common.name"), t("common.phone"), t("account.points"), t("account.sessions_completed")].map((h) => <th key={h} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((c) => (
            <tr key={c.id} data-testid={`customer-row-${c.id}`} className="border-b border-border last:border-0">
              <td className="px-4 py-3 font-medium text-slate-900">{c.name || "—"}</td>
              <td className="px-4 py-3 text-slate-600" dir="ltr">{c.phone}</td>
              <td className="px-4 py-3">{c.loyalty_points || 0}</td>
              <td className="px-4 py-3">{c.completed_paid_sessions || 0}</td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <div className="py-10 text-center text-slate-400">{t("common.none")}</div>}
      </div>
    </div>
  );
}

export function Services() {
  const { t, localize, fmtSAR } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/services").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.services")}</h1>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((s) => (
          <div key={s.id} className="overflow-hidden rounded-xl border border-border bg-white">
            <img src={s.image} alt="" className="h-32 w-full object-cover" />
            <div className="p-4"><div className="font-semibold text-slate-900">{localize(s, "name")}</div>
              <div className="mt-1 flex justify-between text-sm"><span className="text-slate-400">{s.duration_minutes} {t("common.minutes")}</span><span className="font-bold text-primary">{fmtSAR(s.price)}</span></div></div>
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-slate-400">{t("common.edit")}: {new URL(window.location).host} — CRUD API متاح عبر /api/admin/services</p>
    </div>
  );
}

export function Staff() {
  const { t, isAr } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/staff").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.staff")}</h1>
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{[t("common.name"), t("common.email"), t("admin.roles")].map((h) => <th key={h} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((s) => (
            <tr key={s.id} className="border-b border-border last:border-0">
              <td className="px-4 py-3 font-medium">{s.name}</td><td className="px-4 py-3 text-slate-600" dir="ltr">{s.email}</td>
              <td className="px-4 py-3"><span className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">{s.role}</span></td>
            </tr>))}</tbody>
        </table>
      </div>
    </div>
  );
}

export function Payments() {
  const { t, fmtSAR, fmtDate } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.payments")}</h1>
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{[t("common.date"), t("common.total"), "Method", t("common.status")].map((h) => <th key={h} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((p) => (
            <tr key={p.id} className="border-b border-border last:border-0">
              <td className="px-4 py-3 text-slate-600">{fmtDate(p.at).greg}</td><td className="px-4 py-3 font-medium">{fmtSAR(p.amount)}</td>
              <td className="px-4 py-3">{p.method}</td><td className="px-4 py-3"><span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700">{p.status}{p.demo ? " (demo)" : ""}</span></td>
            </tr>))}</tbody>
        </table>
        {rows.length === 0 && <div className="py-10 text-center text-slate-400">{t("common.none")}</div>}
      </div>
    </div>
  );
}

export function Inventory() {
  const { t, localize } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/inventory").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="space-y-10">
      <ProductsAdmin />
      <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.inventory")}</h1>
      <div className="mt-4 overflow-x-auto rounded-xl border border-border bg-white">
        <table className="w-full min-w-[520px] text-sm">
          <thead className="border-b border-border bg-slate-50 text-xs text-slate-500"><tr>{["SKU / " + t("common.name"), t("admin.inventory"), t("common.status")].map((h) => <th key={h} className="px-4 py-3 text-start font-medium">{h}</th>)}</tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-b border-border last:border-0">
              <td className="px-4 py-3 font-medium">{r.product ? localize(r.product, "name") : r.product_id}</td>
              <td className="px-4 py-3">{r.stock}</td>
              <td className="px-4 py-3">{r.stock <= (r.min_stock || 0) ? <span className="rounded-full bg-red-100 px-2 py-1 text-xs text-red-700">{t("common.required")}</span> : <span className="rounded-full bg-green-100 px-2 py-1 text-xs text-green-700">OK</span>}</td>
            </tr>))}</tbody>
        </table>
      </div>
      </div>
    </div>
  );
}

export function Complaints() {
  const { t, fmtDate, isAr } = useI18n();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/complaints").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const resolve = async (id) => { await api.put(`/admin/complaints/${id}`, { status: "resolved" }); load(); };
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.complaints")}</h1>
      <p className="mt-1 text-sm text-slate-500">{isAr ? "التقييم 3 نجوم أو أقل ينشئ تذكرة تلقائياً بمهلة رد 4 ساعات" : "Ratings ≤3 stars auto-create a ticket with a 4-hour SLA"}</p>
      <div className="mt-4 space-y-3">
        {rows.map((c) => (
          <div key={c.id} className="rounded-xl border border-border bg-white p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm">⭐ {c.rating} — {c.comment || "—"}</span>
              <span className={`rounded-full px-2 py-1 text-xs ${c.status === "open" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}>{c.status}</span>
            </div>
            <div className="mt-1 text-xs text-slate-400">{isAr ? "الاستحقاق" : "Due"}: {fmtDate(c.due_at).greg} {fmtDate(c.due_at).time}</div>
            {c.status === "open" && <button onClick={() => resolve(c.id)} className="mt-2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-white">{isAr ? "حل التذكرة" : "Resolve"}</button>}
          </div>
        ))}
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-border py-10 text-center text-slate-400">{t("common.none")}</div>}
      </div>
    </div>
  );
}

export function Reports() {
  const { t, fmtSAR, isAr } = useI18n();
  const [d, setD] = useState(null);
  const [branches, setBranches] = useState([]);
  useEffect(() => {
    api.get("/admin/reports/summary").then((r) => setD(r.data)).catch(() => {});
    api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
  }, []);
  if (!d) return <div className="text-slate-400">{t("common.loading")}</div>;
  const bn = (id) => { const b = branches.find((x) => x.id === id); return b ? (isAr ? b.name_ar : b.name_en) : id; };
  const statusData = Object.entries(d.status_counts || {}).map(([k, v]) => ({ name: k, value: v }));
  const branchData = Object.entries(d.by_branch || {}).map(([k, v]) => ({ name: bn(k), count: v.count, value: Math.round(v.value) }));
  const C = ["#1E40AF", "#EA580C", "#D97706", "#DC2626", "#16a34a"];

  const exportCsv = () => {
    const rows = [["Metric", "Value"], ["Total bookings", d.total_bookings], ["Collected", d.collected], ["Booking value", d.booking_value], ["Refunds", d.refunds], [], ["Branch", "Count", "Value"]];
    branchData.forEach((b) => rows.push([b.name, b.count, b.value]));
    rows.push([], ["Status", "Count"]);
    statusData.forEach((s) => rows.push([s.name, s.value]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "motawahj-report.csv"; a.click();
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-xl font-bold text-slate-900">{t("admin.reports")}</h1>
          <p className="text-xs text-slate-400">{isAr ? "آخر تحديث" : "Updated"}: {new Date(d.generated_at).toLocaleString("en-GB", { timeZone: "Asia/Riyadh" })}</p></div>
        <div className="flex gap-2 print:hidden">
          <button onClick={exportCsv} data-testid="export-csv-btn" className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium hover:border-primary"><FileDown className="h-4 w-4" /> Excel/CSV</button>
          <button onClick={() => window.print()} data-testid="print-report-btn" className="inline-flex items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-white"><Printer className="h-4 w-4" /> PDF</button>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-4 md:grid-cols-4">
        {[[t("admin.kpi_bookings"), d.total_bookings], [t("admin.kpi_revenue"), fmtSAR(d.collected)], [t("admin.kpi_value"), fmtSAR(d.booking_value)], [isAr ? "الاستردادات" : "Refunds", fmtSAR(d.refunds)]].map(([l, v], i) => (
          <div key={i} className="rounded-xl border border-border bg-white p-5"><div className="text-2xl font-extrabold text-slate-900">{v}</div><div className="text-xs text-slate-500">{l}</div></div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <div className="rounded-xl border border-border bg-white p-5">
          <h3 className="mb-4 font-semibold text-slate-900">{isAr ? "الحالة" : "By status"}</h3>
          <ResponsiveContainer width="100%" height={240}><BarChart data={statusData}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis allowDecimals={false} tick={{ fontSize: 11 }} /><Tooltip /><Bar dataKey="value" radius={[6, 6, 0, 0]}>{statusData.map((e, i) => <Cell key={i} fill={C[i % C.length]} />)}</Bar></BarChart></ResponsiveContainer>
        </div>
        <div className="rounded-xl border border-border bg-white p-5">
          <h3 className="mb-4 font-semibold text-slate-900">{isAr ? "حسب الفرع (قيمة)" : "By branch (value)"}</h3>
          <ResponsiveContainer width="100%" height={240}><BarChart data={branchData}><CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" vertical={false} /><XAxis dataKey="name" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => fmtSAR(v)} /><Bar dataKey="value" fill="#EA580C" radius={[6, 6, 0, 0]} /></BarChart></ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

const ROLE_LABELS = {
  super_admin: ["المدير العام", "Super Admin"], branch_manager: ["مدير فرع", "Branch Manager"],
  cashier: ["كاشير", "Cashier"], photographer: ["مصور", "Photographer"],
  customer_service: ["خدمة عملاء", "Customer Service"], accountant: ["محاسب", "Accountant"],
};
export function Roles() {
  const { t, isAr } = useI18n();
  const [d, setD] = useState(null);
  const [role, setRole] = useState("super_admin");
  useEffect(() => { api.get("/admin/roles/matrix").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="text-slate-400">{t("common.loading")}</div>;
  const allowed = (mod) => d.matrix[role] === "all" || (Array.isArray(d.matrix[role]) && d.matrix[role].includes(mod));
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.roles")}</h1>
      <p className="mt-1 text-sm text-slate-500">{isAr ? "اختر دوراً لعرض صلاحياته على الوحدات" : "Select a role to view its module permissions"}</p>
      <div className="mt-4 max-w-xs">
        <label className="text-sm font-medium text-slate-700">{isAr ? "الدور" : "Role"}</label>
        <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="role-select" className="mt-1 w-full rounded-xl border border-border px-4 py-2.5 text-sm">
          {d.roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] ? (isAr ? ROLE_LABELS[r][0] : ROLE_LABELS[r][1]) : r}</option>)}
        </select>
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {d.modules.map((m) => (
          <div key={m} data-testid={`perm-${m}`} className={`flex items-center justify-between rounded-xl border p-4 ${allowed(m) ? "border-green-200 bg-green-50" : "border-border bg-slate-50"}`}>
            <span className="text-sm font-medium capitalize text-slate-700">{t(`admin.${m}`)}</span>
            {allowed(m) ? <span className="rounded-full bg-green-600 px-2 py-0.5 text-xs font-medium text-white">{isAr ? "مسموح" : "Allowed"}</span> : <span className="rounded-full bg-slate-300 px-2 py-0.5 text-xs text-white">✕</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function Audit() {
  const { t, fmtDate } = useI18n();
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.audit")}</h1>
      <div className="mt-4 space-y-2">
        {rows.map((a) => (
          <div key={a.id} className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-2.5 text-sm">
            <span><b className="text-primary">{a.action}</b> · {a.module} {a.target_id ? `(${a.target_id.slice(0, 6)})` : ""}</span>
            <span className="text-xs text-slate-400">{a.actor_name} · {fmtDate(a.at).greg} {fmtDate(a.at).time}</span>
          </div>
        ))}
        {rows.length === 0 && <div className="rounded-xl border border-dashed border-border py-10 text-center text-slate-400">{t("common.none")}</div>}
      </div>
    </div>
  );
}

export function Settings() {
  const { t, isAr } = useI18n();
  const [s, setS] = useState({});
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async (k, v) => { await api.put(`/admin/settings/${k}`, { value: v }); };
  const fields = [["vat_rate", isAr ? "نسبة الضريبة" : "VAT rate"], ["home_fee_sar", isAr ? "رسوم التصوير المنزلي" : "Home fee (SAR)"], ["deposit_pct", isAr ? "نسبة العربون" : "Deposit %"], ["hold_minutes", isAr ? "مدة الحجز المؤقت (دقيقة)" : "Hold minutes"], ["cancel_full_refund_hours", isAr ? "ساعات الاسترداد الكامل" : "Full refund hours"]];
  return (
    <div>
      <h1 className="text-xl font-bold text-slate-900">{t("admin.settings")}</h1>
      <div className="mt-4 max-w-md space-y-4">
        {fields.map(([k, label]) => (
          <div key={k}><label className="text-sm font-medium text-slate-700">{label}</label>
            <input defaultValue={s[k]} onBlur={(e) => save(k, parseFloat(e.target.value))} data-testid={`setting-${k}`} className="mt-1 w-full rounded-xl border border-border px-4 py-2.5" /></div>
        ))}
        <p className="text-xs text-slate-400">{isAr ? "التغييرات تُحفظ عند مغادرة الحقل" : "Changes save on blur"}</p>
      </div>
    </div>
  );
}

export function Loyalty() {
  const { isAr } = useI18n();
  return (
    <div className="space-y-10">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{isAr ? "الولاء والتسويق" : "Loyalty & Marketing"}</h1>
        <div className="mt-4 rounded-xl bg-primary/5 p-4 text-sm text-primary">{isAr ? "كل 3 جلسات مدفوعة = جلسة مجانية · نقطة لكل 10 ر.س" : "Every 3 paid sessions = free session · 1 point per 10 SAR"}</div>
      </div>
      <CouponsAdmin />
      <MembershipsAdmin />
    </div>
  );
}
